import { notification } from "antd";
import GeneralCosts from "entities/GeneralCosts";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { SEND_FINALIZE_SUBSCRIPTION } from "utils/api/graphql/mutations/webhook";
import { PROJECT } from "utils/api/graphql/queries/projects";
import { CONTACT_POINT_SYSTEM, NAVIGATION_DIRECTIONS, TEMPLATE_TYPES } from "utils/constants";
import { v4 } from "uuid";
import View from "./View";

const SendLink = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.SUBSCRIPTION);
  const SEND_LINK = t("SEND_LINK", { returnObjects: true });
  const { state } = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);

  const { data, loading } = useQuery(PROJECT, {
    variables: { where: { id: state?.projectId } },
  });

  const [getSubscription] = useLazyQuery(graphql.queries.SUBSCRIPTION);

  const [sendSubscriptionLink] = useMutation(
    graphql.mutations.SEND_SUBSCRIPTION_LINK,
    {
      variables: {
        data: {
          project: { id: state?.projectId },
          insurancePlan: { id: get(state, "insurancePlanIds.0", null) },
          link: state?.link,
        },
      },
      onCompleted: (_) => {
        setIsProcessing(false);
        navigate(NAVIGATION_DIRECTIONS.BACK);
        notification.open({
          message: SEND_LINK.SUCCESS,
          duration: 5,
          type: "success",
        });
      },
      onError: (error) => {
        setIsProcessing(false);
        console.error("Error sending subscription link:", error);
        notification.error({
          message: "Erreur envoi lien",
          description: error.message,
        });
      },
    }
  );

  const [sendFinalizeSubscription] = useMutation(SEND_FINALIZE_SUBSCRIPTION, {
    onCompleted: () => { },
    onError: (error) => {
      console.error("Error sending LMF finalize subscription:", error);
      notification.error({
        message: "Erreur LMF webhook",
        description: error.message,
      });
    },
  });

  const [updateContact] = useMutation(graphql.mutations.UPDATE_CONTACT, {
    onCompleted: async () => {
      if (isProcessing) {
        return;
      }
      setIsProcessing(true);

      try {
        const processTemplate = get(data, "project.fields.processTemplate", "");
        const isLMF = processTemplate === TEMPLATE_TYPES.B2B_LMF;

        if (isLMF) {
          const subscriptions = get(data, "project.subscriptions", []);
          const targetSubscription = subscriptions.find(subscription =>
            get(subscription, "insurancePlan.id") === get(state, "insurancePlanIds.0")
          );

          const contactTelecoms = get(data, "project.contact.telecoms", []);
          const emailTelecom = contactTelecoms.find(
            telecom => telecom.system === CONTACT_POINT_SYSTEM.EMAIL
          );
          const email = emailTelecom?.value;

          if (email) {
            const targetCost = window.lmfCostData;

            const products = [{
              label: get(targetCost, "insurancePlan.name", get(targetSubscription, "insurancePlan.name", "")),
              price: targetCost ? `${targetCost.cost}€/mois` : "",
              url: state?.link || ""
            }];

            const notificationData = {
              recipient: email,
              externalId: v4(),
              language: "FR",
              data: {
                lastName: get(data, "project.contact.user.lastname", ""),
                firstName: get(data, "project.contact.user.firstname", ""),
                products,
                agentFirstName: get(data, "project.author.firstname", "Camille"),
                agentEmail: get(data, "project.author.email", "camille@mutuelle-familiale.fr"),
              },
            };

            await sendFinalizeSubscription({
              variables: {
                notification: notificationData,
              },
            });

            delete window.lmfCostData;
          }

          setIsProcessing(false);
          navigate(NAVIGATION_DIRECTIONS.BACK);
          notification.open({
            message: SEND_LINK.SUCCESS,
            duration: 5,
            type: "success",
          });
        } else {
          await sendSubscriptionLink();
        }

      } catch (error) {
        console.error("Error in subscription link logic:", error);
        setIsProcessing(false);
        const processTemplate = get(data, "project.fields.processTemplate", "");
        const isLMF = processTemplate === TEMPLATE_TYPES.B2B_LMF;

        if (!isLMF) {
          await sendSubscriptionLink();
        } else {
          notification.error({
            message: "Erreur LMF webhook",
            description: error.message,
          });
        }
      }
    },
    onError: (error) => {
      setIsProcessing(false);
      console.error("Error updating contact:", error);
      notification.error({
        message: "Erreur mise à jour contact",
        description: error.message,
      });
    },
  });

  const sendLink = (telecoms, costsData) => {
    const processTemplate = get(data, "project.fields.processTemplate", "");
    const isLMF = processTemplate === TEMPLATE_TYPES.B2B_LMF;

    if (isLMF && costsData && costsData.length > 0) {
      const targetCost = costsData.find(cost =>
        get(cost, "insurancePlan.id") === get(state, "insurancePlanIds.0")
      );

      if (targetCost) {
        window.lmfCostData = targetCost;
      }
    }

    updateContact({
      variables: {
        where: {
          id: get(data, "project.contact.id", []),
        },
        data: {
          telecoms: [
            ...omitDeep(
              get(data, "project.contact.telecoms", []),
              "__typename"
            ).filter(
              (telecom) => telecom.system !== CONTACT_POINT_SYSTEM.EMAIL
            ),
            ...telecoms.map(({ label, value }) => ({
              id: value,
              system: CONTACT_POINT_SYSTEM.EMAIL,
              value: label,
            })),
          ],
        },
      },
    });
  };

  const generalCostsFilter = {
    where: {
      project: {
        id: state?.projectId,
      },
    },
    isIn: {
      insurancePlan: {
        id: state?.insurancePlanIds || [],
      },
    },
  };

  if (loading) return <Loading />;

  return (
    <GeneralCosts
      filter={generalCostsFilter}
      updateContact={updateContact}
      onSubmit={sendLink}
      View={View}
      NoData={View}
      link={state?.link}
      project={omitDeep(get(data, "project", {}), "__typename")}
    />
  );
};

export default SendLink;
